import { uniq } from 'lodash';
import { useRouter } from 'next/router';
import { ParsedUrlQuery } from 'querystring';
import { useCallback, useMemo, useReducer } from 'react';

import { SearchSvg } from '../assets/icons';
import {
  ContentCategory,
  contentHierarchy,
  fall24ContentHierarchy,
} from '../utils/authenticatedHomeUtils';
import useMixpanelFlag from './useMixpanelFlag';

const searchStateToRouterQuery = (state: SearchReducerstate) => {
  const searchParams: {
    searchPhrase?: string;
    categories?: string;
    grades?: string;
    subjects?: string;
  } = {};

  if (state.searchPhrase) {
    searchParams.searchPhrase = state.searchPhrase;
  }

  if (state.subjects.length > 0) {
    searchParams.subjects = state.subjects.join(',');
  }
  if (state.categories.length > 0) {
    searchParams.categories = state.categories.join(',');
  }
  if (state.grades.length > 0) {
    searchParams.grades = state.grades.join(',');
  }
  return searchParams;
};

export type SearchReducerstate = {
  searchPhrase?: string;
  categories: string[];
  grades: string[];
  subjects: string[];
};
type SearchReducerAction = {
  type: 'alterFilter' | 'clearFilters';
  payload?: {
    searchPhrase?: string;
    categories?: string[];
    grades?: string[];
    subjects?: string[];
  };
};

const useGeneratorsQueryState = () => {
  const router = useRouter();

  const [searchState, dispatchSearchState] = useReducer(
    searchReducer,
    getInitialSearchState(router.query),
  );

  const isFall24ContentHierarchyEnabled = useMixpanelFlag('FALL_24_CONTENT_HIERARCHY');

  const clearFilters = () => {
    // removing the params from the router
    const routerQuery = { ...router.query };
    delete routerQuery.subjects;
    delete routerQuery.grades;
    delete routerQuery.categories;
    delete routerQuery.searchPhrase;

    dispatchSearchState({
      type: 'clearFilters',
    });
    router.push({
      pathname: router.pathname,
      query: {
        ...routerQuery,
      },
    });
  };

  const handleGeneratorUrlSearchChange = useCallback(
    ({
      subjectToggled,
      gradeToggled,
      categoryToggled,
      newSearchPhrase,
    }: {
      subjectToggled?: string;
      gradeToggled?: string;
      categoryToggled?: string;
      newSearchPhrase?: string;
    }) => {
      // Take existing search state and change the thing that changed

      const searchStateCopy = { ...searchState };

      // NEW

      if (gradeToggled) {
        // checks if the grade is already in the grades array
        const gradeExists = searchStateCopy.grades.includes(gradeToggled);
        // if the grade exists, it removes the grade from the array

        searchStateCopy.grades = gradeExists
          ? searchStateCopy.grades.filter(grade => grade !== gradeToggled)
          : [...searchStateCopy.grades, gradeToggled];
      }

      if (categoryToggled) {
        if (categoryToggled === 'for_you' || categoryToggled === 'none') {
          searchStateCopy.categories = [];
        } else {
          searchStateCopy.categories = [categoryToggled];
        }
      }

      if (subjectToggled) {
        // checks if the subject is already in the subjects array
        // Support only one subject
        const subjectExists = searchStateCopy.subjects.includes(subjectToggled);

        searchStateCopy.subjects = subjectExists
          ? searchStateCopy.subjects.filter(subject => subject !== subjectToggled)
          : [subjectToggled];

        // Whenever we change the subject - we always have to reset the categories
        searchStateCopy.categories = [];
      }

      // SEARCH PHRASE
      if (newSearchPhrase || newSearchPhrase === '') {
        searchStateCopy.searchPhrase = newSearchPhrase;
      } else {
        searchStateCopy.searchPhrase = searchStateCopy.searchPhrase;
      }

      // Perform the state change and router change
      dispatchSearchState({
        type: 'alterFilter',
        payload: searchStateCopy,
      });

      // Sync with the router
      router.replace({
        pathname: router.pathname,
        query: {
          tenantId: router.query.tenantId, // need tenantId because nextjs has params in the query
          ...searchStateToRouterQuery(searchStateCopy),
        },
      });
    },
    [router, searchState],
  );

  const activeContentMap = useMemo(() => {
    const hierarchy = isFall24ContentHierarchyEnabled
      ? fall24ContentHierarchy
      : contentHierarchy;
    const activeContentMap = hierarchy.find(subjectData => {
      // When no subject is selected we default to "none" in the content hierarchy
      if (searchState.subjects.length === 0) return subjectData.subject === 'none';
      if (subjectData.subject === searchState.subjects[0]) {
        return subjectData;
      }
    });

    if (!activeContentMap) return hierarchy[0];

    if (searchState.searchPhrase) {
      // Hack to show "Your search instead of for you"
      const injectedCategories = activeContentMap.categories.map(category => {
        if (category.id === 'for_you') {
          return {
            id: 'none',
            title: 'Your search',
            icon: <SearchSvg />,
            subCategories: [],
          };
        } else return category;
      });
      return {
        ...activeContentMap,
        categories: injectedCategories,
      };
    }

    return activeContentMap;
  }, [searchState.searchPhrase, searchState.subjects, isFall24ContentHierarchyEnabled]);

  const activeCategory = useMemo(() => {
    const currentActive = activeContentMap?.categories.find(category => {
      if (searchState.categories.length === 0) return category.id === 'for_you';
      return searchState.categories.includes(category.id);
    });
    if (searchState.searchPhrase && currentActive?.id === 'for_you') {
      // Hack to show "Your search instead of for you"
      return {
        id: 'none',
        title: 'Your search',
        icon: <SearchSvg />,
        subCategories: [],
      };
    }
    if (currentActive) {
      return currentActive;
    }

    return activeContentMap.categories[0] as ContentCategory | undefined;
  }, [activeContentMap, searchState.categories, searchState.searchPhrase]);

  return {
    grades: searchState.grades,
    subjects: searchState.subjects,
    searchPhrase: searchState.searchPhrase,
    activeCategory,
    activeContentMap,
    visibleCategories: activeContentMap?.categories || [],
    clearFilters,
    handleGeneratorUrlSearchChange,
    searchState,
  };
};
const getSearchPhrasefromQuery = (query: string | undefined) => {
  if (query) {
    const searchPhrase = query;
    return searchPhrase;
  }
  return undefined;
};
const getGradefromQuery = (query: string | undefined) => {
  if (query) {
    const grades = query.split(',');
    return uniq(grades);
  }
  return [];
};

const getSubjectfromQuery = (query: string | undefined) => {
  if (query) {
    const subjects = query.split(',');
    return uniq(subjects);
  }
  return [];
};

const getCategoryfromQuery = (query: string | undefined) => {
  if (query) {
    const subjects = query.split(',');
    return uniq(subjects);
  }
  return [];
};

const getInitialSearchState = (routerQuery: ParsedUrlQuery) => {
  const subjects = routerQuery.subjects as string;
  const grades = routerQuery.grades as string;
  const categories = routerQuery.categories as string;
  const searchPhrase = routerQuery.searchPhrase as string;

  return {
    subjects: getSubjectfromQuery(subjects),
    grades: getGradefromQuery(grades),
    categories: getCategoryfromQuery(categories),
    searchPhrase: getSearchPhrasefromQuery(searchPhrase),
  };
};

const searchReducer = (state: SearchReducerstate, action: SearchReducerAction) => {
  switch (action.type) {
    case 'alterFilter':
      return {
        ...state,
        searchPhrase: action.payload?.searchPhrase || undefined,
        grades: action.payload?.grades || state.grades,
        categories: action.payload?.categories || state.categories,
        subjects: action.payload?.subjects || state.subjects,
      };
    case 'clearFilters':
      return {
        ...state,
        searchPhrase: undefined,
        grades: [],
        categories: [],
        subjects: [],
      };
    default:
      return state;
  }
};

export default useGeneratorsQueryState;
